import { Theme, ThemeConsumer } from '@rneui/themed';
import React, { useEffect, useMemo, useState } from 'react';
import {
	View,
	TextInput,
	TouchableOpacity,
	StyleSheet,
	Keyboard,
	useWindowDimensions,
} from 'react-native';
import { ZenText, ZenToast } from 'zen-core-elements';
import SellscreenModal from '../sellscreenModal';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { updateCartProductQuantity } from '../../../redux/slices/newSellscreenSlice';
import { appColors } from 'zen-core-elements/src/shared/constants';

export default function HybridQtyInput({ variantId, quantity, stock, weightCount, sellScreenType }: {
	variantId: number
	weightCount: number
	stock: number
	sellScreenType: "SELL" | "RETURN" | "REPLACEMENT"
	quantity: number
}) {
	const [isModalOpen, setModalOpen] = useState(false);
	const dispatch = useAppDispatch()
	const { id: locationId } = useAppSelector((state) => state?.common?.currentLocation)
	const { width } = useWindowDimensions()
	const [qty, setQty] = useState<number | string>(quantity)


	const QUICK_OPTS = useMemo(() => {
		return Array.from({ length: 20 }).map((_, i) => (i + 1) * weightCount)
	}, [weightCount])

	const handleQuickSelect = (quantity: number) => {
		setQty(() => quantity)
		setModalOpen(false);
		Keyboard.dismiss();
	};
	useEffect(() => {
		setQty(quantity)
	}, [quantity])

	useEffect(() => {
		if (qty && qty !== quantity && Number(qty) % weightCount == 0) {
			updateQuantity()
		}
	}, [qty])

	const updateQuantity = () => {
		const correctStock = stock == -1 ? Infinity : stock
		if (Number(qty) <= correctStock) {
			dispatch(updateCartProductQuantity({ variantId, quantity: Number(qty), locationId }))
		} else {
			ZenToast({
				type: "warning",
				message: `Stock limit exceeded (Max: ${stock})`
			})
			setQty(() => quantity)
		}
	}

	const handleTextChange = (text: string) => {
		if (/^\d*$/.test(text)) {
			setQty(text)
		}
	};

	const onBlur = () => {
		if (!qty || qty == 0 || Number(qty) % weightCount != 0)
			setQty(() => quantity)
	}

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<View style={styles.wrapper}>
						<View style={[styles.hybridContainer, sellScreenType === "RETURN" && { opacity: 0.7 }]}>

							<TextInput
								style={styles.directInput}
								value={qty?.toString()}
								onBlur={onBlur}
								onChangeText={handleTextChange}
								keyboardType="numeric"
								selectTextOnFocus={true}
								editable={sellScreenType !== "RETURN"}
							/>

							<View style={styles.divider} />

							<TouchableOpacity
								disabled={sellScreenType === "RETURN"}
								style={[styles.dropdownTrigger, sellScreenType === "RETURN" && { opacity: 0.7 }]}
								onPress={() => setModalOpen(true)}
								activeOpacity={0.6}
							>
								<ZenText content="▼" style={styles.chevron} />
							</TouchableOpacity>

						</View>

						<SellscreenModal isModalOpen={isModalOpen} setModalOpen={setModalOpen} viewStyle={width > 900 && { width: 400 }}>
							<ZenText content="Select a Quantity" style={styles.pickerHeader} />
							<View style={styles.grid}>
								{QUICK_OPTS.map((num) => {
									const isSelected = num === quantity
									const isNotAvailable = num > (stock == -1 ? Infinity : stock)
									return (
										<TouchableOpacity
											key={num}
											disabled={isNotAvailable}
											style={[
												styles.gridItem,
												isSelected && styles.gridItemSelected,
												isNotAvailable && { opacity: 0.5 }
											]}
											onPress={() => handleQuickSelect(num)}
										>
											<ZenText content={num} style={[
												styles.gridText,
												isSelected && styles.gridTextSelected
											]} />
										</TouchableOpacity>
									)
								})}
							</View>
						</SellscreenModal>

					</View>
				)
			}}
		</ThemeConsumer>
	);
}

const style = (theme: Theme) => (
	StyleSheet.create({
		wrapper: {
			flex: 1.5,
			alignItems: 'center',
		},
		label: {
			fontSize: theme?.miniLabel,
			color: theme?.grey,
			marginBottom: 4,
			fontFamily: theme?.medium,
			textTransform: 'uppercase',
		},

		hybridContainer: {
			flexDirection: 'row',
			alignItems: 'center',
			backgroundColor: theme?.light,
			borderWidth: 1,
			borderColor: theme?.coolGray,
			borderRadius: 8,
			height: 40,
			width: '80%',

			shadowColor: theme?.disabledGray500,
			shadowOpacity: 0.04,
			shadowRadius: 4,
			elevation: 2,
			overflow: 'hidden',
		},

		directInput: {
			flexGrow: 1,
			fontSize: theme?.smallFontSize,
			fontFamily: theme?.medium,
			color: theme?.lightDark,
			paddingHorizontal: 12,
			paddingVertical: 0,
			height: '100%',
		},

		divider: {
			width: 1,
			height: '60%',
			backgroundColor: theme?.coolGray,
		},

		dropdownTrigger: {
			width: 36,
			height: '100%',
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: theme?.iceWhite,
		},
		chevron: {
			top: -2,
			fontSize: 18,
			color: theme?.grey,
		},

		pickerHeader: {
			fontSize: theme?.errorFontSize,
			fontFamily: theme?.bold,
			color: theme?.lightDark,
			textTransform: 'uppercase',
			marginBottom: 12,
			textAlign: 'center',
		},
		grid: {
			flexDirection: 'row',
			flexWrap: 'wrap',
			gap: 8,
			justifyContent: 'center',
		},
		gridItem: {
			width: 55,
			height: 55,
			borderRadius: 8,
			borderWidth: 1,
			borderColor: theme?.coolGray,
			backgroundColor: theme?.light,
			alignItems: 'center',
			justifyContent: 'center',
		},
		gridItemSelected: {
			borderColor: theme.blue600,
			backgroundColor: theme?.blue50,
		},
		gridText: {
			fontSize: theme?.errorFontSize,
			fontFamily: theme?.medium,
			color: theme?.lightDark
		},
		gridTextSelected: {
			color: theme?.blue600,
			fontFamily: theme?.bold
		},
	})
)